import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, Search, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { resolveImageUrl } from './ImageUpload';
import { priceWithTier } from '../lib/pricing';
import Rial from './Rial';

/**
 * Per-customer manual price overrides editor.
 * Opens inside the existing AdminCustomers edit drawer so the admin can:
 *   - see every product with catalog price + the tier-discounted price
 *   - set a flat override price for any product (clears tier discount)
 *   - leave the input empty to revert to the tier price
 * Saves the entire overrides map as a single bulk PUT.
 */
export const CustomerPriceOverridesPanel = ({ email, discountPct = 0, api, authHeaders }) => {
  const [products, setProducts] = useState([]);
  const [overrides, setOverrides] = useState({}); // { productId: stringValue }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [prodRes, overrideRes] = await Promise.all([
          axios.get(`${api}/products`),
          axios.get(`${api}/admin/customers/${encodeURIComponent(email)}/price-overrides`, { headers: authHeaders }),
        ]);
        if (cancelled) return;
        setProducts(prodRes.data || []);
        const raw = overrideRes.data?.price_overrides || {};
        // Keep values as strings in form state so the input can stay empty
        // when the admin clears a value (vs jumping to 0).
        const asStrings = {};
        for (const [k, v] of Object.entries(raw)) asStrings[k] = String(v);
        setOverrides(asStrings);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load overrides', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [email, api, authHeaders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.size || '').toLowerCase().includes(q) ||
        (p.category_slug || '').toLowerCase().includes(q),
    );
  }, [products, search]);

  const overrideCount = useMemo(
    () => Object.values(overrides).filter((v) => v !== '' && Number.isFinite(Number(v))).length,
    [overrides],
  );

  const setOverride = (productId, raw) => {
    setSaved(false);
    setOverrides((prev) => {
      const next = { ...prev };
      if (raw === '' || raw === null || raw === undefined) {
        delete next[productId];
      } else {
        next[productId] = raw;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    // Only keep entries that parse as a finite non-negative number
    const payload = {};
    for (const [pid, raw] of Object.entries(overrides)) {
      const num = Number(raw);
      if (Number.isFinite(num) && num >= 0) payload[pid] = num;
    }
    try {
      await axios.put(
        `${api}/admin/customers/${encodeURIComponent(email)}/price-overrides`,
        { price_overrides: payload },
        { headers: authHeaders },
      );
      setSaved(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const clearAll = () => {
    if (!window.confirm('Remove every manual price override for this customer?')) return;
    setOverrides({});
    setSaved(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-neutral-500 py-3">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading products…
      </div>
    );
  }

  return (
    <div data-testid="customer-price-overrides-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-[11px] text-neutral-500">
          {overrideCount > 0 ? (
            <span data-testid="override-count">
              <strong className="text-emerald-700">{overrideCount}</strong> manual price{overrideCount === 1 ? '' : 's'} set
            </span>
          ) : (
            <span>No manual prices yet — leave a field blank to keep the tier-discounted price.</span>
          )}
        </div>
        {overrideCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-[11px] text-red-600 hover:underline font-semibold"
            data-testid="clear-all-overrides-btn"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products by name, size or division…"
          className="h-9 pl-9 pr-9 text-xs"
          data-testid="overrides-search"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-100 rounded-full"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5 text-neutral-400" />
          </button>
        )}
      </div>

      <div
        className="max-h-[320px] overflow-y-auto border border-neutral-200 rounded-xl divide-y divide-neutral-100 bg-white"
        data-testid="overrides-product-list"
      >
        {filtered.length === 0 && (
          <div className="px-4 py-6 text-xs text-neutral-500 text-center">No products match "{search}".</div>
        )}
        {filtered.map((p) => {
          const img = resolveImageUrl(p.image);
          const tierPrice = priceWithTier(p.price, discountPct);
          const overrideRaw = overrides[p.id] ?? '';
          const overrideNum = Number(overrideRaw);
          const hasValidOverride = overrideRaw !== '' && Number.isFinite(overrideNum) && overrideNum >= 0;
          return (
            <div key={p.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="w-9 h-9 rounded-md bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                {img && <img src={img} alt={p.name} className="max-w-full max-h-full object-contain" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-[#0F172A] truncate" title={p.name}>{p.name}</div>
                <div className="text-[10px] text-neutral-500">
                  Catalog <Rial /> {Number(p.price).toFixed(3)}
                  {discountPct > 0 && (
                    <>
                      <span className="mx-1">·</span>
                      Tier price <Rial /> {tierPrice.toFixed(3)}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-[10px] font-bold text-neutral-400 uppercase"><Rial /></span>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder={tierPrice.toFixed(3)}
                  value={overrideRaw}
                  onChange={(e) => setOverride(p.id, e.target.value)}
                  className={`h-8 w-24 text-xs text-right ${hasValidOverride ? 'border-emerald-400 bg-emerald-50/60 font-semibold text-emerald-700' : ''}`}
                  data-testid={`override-input-${p.id}`}
                />
                {hasValidOverride && (
                  <button
                    type="button"
                    onClick={() => setOverride(p.id, '')}
                    className="p-1 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Clear override"
                    data-testid={`override-clear-${p.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="text-[11px] text-neutral-500">
          {saved && <span className="text-emerald-600 font-semibold">Saved ✓</span>}
        </div>
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-full text-xs h-9"
          data-testid="save-overrides-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save manual prices
        </Button>
      </div>
    </div>
  );
};

export default CustomerPriceOverridesPanel;
